import { useState, useEffect } from "react";
import { getDashboard, getSalons } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  IndianRupee, 
  CreditCard, 
  Banknote, 
  Users,
  TrendingUp,
  Receipt
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { format } from "date-fns";

const COLORS = ["#2D4A43", "#4A7A6F", "#6BA399", "#8ECDC2"];

const Dashboard = () => {
  const [salons, setSalons] = useState([]);
  const [selectedSalon, setSelectedSalon] = useState("all");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSalons();
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [selectedSalon, days]);

  const fetchSalons = async () => {
    try {
      const response = await getSalons();
      setSalons(response.data);
    } catch (error) {
      console.error("Failed to fetch salons:", error);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (selectedSalon !== "all") {
        params.salon_id = selectedSalon;
      }
      const response = await getDashboard(params);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const pieData = data ? [
    { name: "Service", value: data.sales_by_category.service },
    { name: "Product", value: data.sales_by_category.product },
    { name: "Membership", value: data.sales_by_category.membership },
    { name: "Misc", value: data.sales_by_category.misc },
  ].filter(item => item.value > 0) : [];

  const chartData = data?.daily_trend?.map(item => ({
    ...item,
    date: format(new Date(item.date), "MMM d"),
  })) || [];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your salon performance</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={selectedSalon} onValueChange={setSelectedSalon} data-testid="salon-filter">
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Salons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Salons</SelectItem>
              {salons.map((salon) => (
                <SelectItem key={salon.id} value={salon.id}>{salon.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))} data-testid="days-filter">
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Sales</p>
                <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-sales">
                  {formatCurrency(data?.total_sales || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cash Sales</p>
                <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="cash-sales">
                  {formatCurrency(data?.total_cash || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Banknote className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Card/UPI Sales</p>
                <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="card-sales">
                  {formatCurrency(data?.total_card || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Walk-ins</p>
                <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-walkins">
                  {(data?.total_male_walkins || 0) + (data?.total_female_walkins || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  M: {data?.total_male_walkins || 0} | F: {data?.total_female_walkins || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Bill Value (ABV)</p>
                <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-abv">
                  {formatCurrency(data?.abv_total || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  M: {formatCurrency(data?.abv_male || 0)} | F: {formatCurrency(data?.abv_female || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Receipt className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Trend */}
        <Card className="lg:col-span-2 border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Sales Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2D4A43" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#2D4A43" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `₹${v/1000}k`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: "white", 
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)"
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#2D4A43" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorTotal)" 
                    name="Total Sales"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No sales data available for this period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sales by Category */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Sales by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No category data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Sales */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Recent Sales</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.recent_sales?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Salon</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Total</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Cash</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Card</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-700">Walk-ins</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_sales.map((sale, index) => (
                    <tr key={index} className="border-b border-border/50 hover:bg-accent/50 transition-colors">
                      <td className="py-3 px-4">{format(new Date(sale.date), "MMM d, yyyy")}</td>
                      <td className="py-3 px-4">{sale.salon_name}</td>
                      <td className="py-3 px-4 text-right font-medium">{formatCurrency(sale.today_total)}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(sale.today_total - sale.card_sale)}</td>
                      <td className="py-3 px-4 text-right text-blue-600">{formatCurrency(sale.card_sale)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-blue-600">M:{sale.male_walkins}</span>
                        {" / "}
                        <span className="text-pink-600">F:{sale.female_walkins}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No recent sales data. Start by adding a daily sale entry.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
